import React, { Fragment, useEffect, useState } from "react";
import ServicePlanAPI from '~/api/servicePlanApi';
import api from "~/api/api";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { CenteredModal, AddCardForm } from '~/components';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import styles from './UpgradePlanModal.module.scss';
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_placeholder");

function UpgradePlanModal({ show, onClose }) {
    const [serviceType, setServiceType] = useState(1); // 1 = Personal, 2 = Business
    const [servicePlans, setServicePlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
    const [isLoadingPaymentInfo, setIsLoadingPaymentInfo] = useState(false);

    const [showAddModal, setShowAddModal] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [isCreatingIntent, setIsCreatingIntent] = useState(false);

    // New states for plan change confirmation
    const [showConfirmPlanChange, setShowConfirmPlanChange] = useState(false);
    const [confirmPlanData, setConfirmPlanData] = useState({ text: '' });

    const handleOpenAddModal = async () => {
        try {
            setIsCreatingIntent(true);
            const res = await api.post('/user/stripe-payments/create-setup-intent');
            setClientSecret(res.data.ClientSecret);
            setShowAddModal(true);
        } catch (err) {
            toast.error("Failed to initialize card setup.");
        } finally {
            setIsCreatingIntent(false);
        }
    };

    const handleSaveSuccess = () => {
        setShowAddModal(false);
        setClientSecret(null);
        setHasPaymentMethod(true);
        toast.success(<span className="fs-9 fw-bold">Card added successfully! You can now continue with your plan.</span>);
    };

    useEffect(() => {
        if (show) {
            // Check payment methods
            setIsLoadingPaymentInfo(true);
            api.get('/user/stripe-payments/payment-methods')
                .then(res => {
                    setHasPaymentMethod(res.data && res.data.length > 0);
                })
                .catch(err => console.error("Error fetching payment methods", err))
                .finally(() => setIsLoadingPaymentInfo(false));

            ServicePlanAPI.getByServiceType(serviceType)
                .then((res) => {
                    setServicePlans(res.data);
                    if (res.data.length > 0 && !selectedPlanId) {
                        setSelectedPlanId(res.data[0].ServicePlanId);
                    } else if (res.data.length > 0) {
                        // Ensure the selectedPlanId belongs to the current serviceType
                        const exists = res.data.find(p => p.ServicePlanId === selectedPlanId);
                        if (!exists) {
                            setSelectedPlanId(res.data[0].ServicePlanId);
                        }
                    }
                })
                .catch((err) => {
                    console.error("Error fetching service plans", err);
                });
        }
    }, [serviceType, show]);

    const handleSelectPlan = (id) => {
        setSelectedPlanId(id);
    };

    const handleContinue = async () => {
        if (!selectedPlanId) return;

        const selectedService = servicePlans.find(p => p.ServicePlanId === selectedPlanId);
        
        // Handle Free Plan (ID = 1, usually)
        if (selectedPlanId === 1 || (selectedService && selectedService.Price === 0)) {
            setIsProcessing(true);
            ServicePlanAPI.checkExistFreePlan()
                .then((res) => {
                    const status = res.data.Status;
                    if (status === 0) { // Eligible
                        ServicePlanAPI.confirmFreePlan()
                            .then(() => {
                                toast.success(<span className="fs-9 fw-bold">Free plan activated successfully!</span>);
                                window.location.href = '/ai-chatbot/chat';
                            })
                            .catch((err) => {
                                const errMsg = err.response?.data?.Message || "An error occurred while registering the free plan.";
                                toast.error(<span className="fs-9 fw-bold">{errMsg}</span>);
                            })
                            .finally(() => setIsProcessing(false));
                    } else if (status === 1) { // Used Free Plan
                        toast.error(<span className="fs-9 fw-bold">You have already used the Free plan. Please select a paid plan.</span>);
                        setIsProcessing(false);
                    } else if (status === 2) { // Active Paid Plan
                        toast.error(<span className="fs-9 fw-bold">You currently have an active paid plan. You cannot switch to the Free plan until your current plan expires.</span>);
                        setIsProcessing(false);
                    }
                })
                .catch(() => {
                    toast.error(<span className="fs-9 fw-bold">Could not check free plan status.</span>);
                    setIsProcessing(false);
                });
            return;
        }

        // Handle Paid Plan
        if (!hasPaymentMethod) {
            handleOpenAddModal();
            return;
        }

        const proceedToCheckout = async () => {
            setIsProcessing(true);
            try {
                const response = await api.post(`/user/stripe-payments/create-checkout-session/${selectedPlanId}/${serviceType}`);
                window.location.href = response.data.Url;
            } catch (error) {
                toast.error(<span className="fs-9 fw-bold">Failed to initiate checkout.</span>);
                setIsProcessing(false);
            }
        };

        // Check if user has an active paid plan before checking out
        setIsProcessing(true);
        ServicePlanAPI.checkExistFreePlan()
            .then((res) => {
                setIsProcessing(false);
                if (res.data && res.data.Status === 2) {
                    setConfirmPlanData({
                        text: `You are currently using the ${res.data.ActivePlanName || 'Paid'} plan (expires ${new Date(res.data.ActivePlanEndDate).toLocaleDateString()}). If you continue to purchase a new plan, your current plan and its remaining time will be canceled. Do you want to proceed?`
                    });
                    setShowConfirmPlanChange(true);
                } else {
                    proceedToCheckout();
                }
            })
            .catch(() => {
                setIsProcessing(false);
                proceedToCheckout();
            });
    };

    const selectedService = servicePlans.find(p => p.ServicePlanId === selectedPlanId);

    return (
        <Fragment>
            <CenteredModal 
            show={show} 
            onClose={onClose} 
            modalType="modal-xl"
            modalContentClass={cx('custom-modal-content')}
            title=""
            closeText=""
        >
            <div className="p-0 position-relative" style={{ filter: showAddModal ? 'blur(4px) brightness(0.9)' : 'none', transition: 'all 0.2s ease', pointerEvents: showAddModal ? 'none' : 'auto' }}>
                <button 
                    onClick={onClose} 
                    className="btn btn-link position-absolute top-0 end-0 p-3 text-dark z-3"
                    style={{ zIndex: 10 }}
                >
                    <FontAwesomeIcon icon={faTimes} className="fs-6 opacity-75" />
                </button>
                <div className="row g-0">
                    {/* Left Column: Plan Selection */}
                    <div className="col-lg-7 p-4 p-md-5 bg-white rounded-start-4">
                        <h4 className="fw-bolder mb-4">
                            Upgrade to the {selectedService ? selectedService.Name : ''} plan to access more features
                        </h4>
                        
                        {/* Tabs for Personal / Business */}
                        <ul className="nav nav-pills mb-4" id="pills-tab" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button 
                                    className={`nav-link rounded-pill px-4 py-2 fs-9 fw-bold ${serviceType === 1 ? 'active bg-primary text-white' : 'text-body-tertiary'}`} 
                                    onClick={() => setServiceType(1)}
                                >
                                    Personal
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button 
                                    className={`nav-link rounded-pill px-4 py-2 fs-9 fw-bold ${serviceType === 2 ? 'active bg-primary text-white' : 'text-body-tertiary'}`} 
                                    onClick={() => setServiceType(2)}
                                >
                                    Business
                                </button>
                            </li>
                        </ul>

                        <div className="d-flex flex-column gap-3 mb-5">
                            {servicePlans.map((plan) => (
                                <div 
                                    key={plan.ServicePlanId}
                                    className={cx('plan-card', { 'selected': selectedPlanId === plan.ServicePlanId })}
                                    onClick={() => handleSelectPlan(plan.ServicePlanId)}
                                >
                                    <div className="d-flex align-items-center">
                                        <div className={cx('radio-btn', { 'checked': selectedPlanId === plan.ServicePlanId })}>
                                            <div className={cx('radio-inner')}></div>
                                        </div>
                                        <div className="ms-3">
                                            <h6 className="m-0 fw-bold d-flex align-items-center gap-2">
                                                {plan.Name} 
                                                <span className="fs-9 fw-bold text-dark">${plan.Price}</span> 
                                                <span className="text-body-tertiary fs-10 fw-normal">per {plan.DurationUnit.toLowerCase()}</span>
                                            </h6>
                                            <p className="m-0 fs-9 text-body-secondary mt-1">{plan.Description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {selectedService && (
                            <button 
                                className={cx('btn-continue', { 'btn-warning': !hasPaymentMethod && selectedService.Price > 0 })} 
                                onClick={handleContinue}
                                disabled={isProcessing || isLoadingPaymentInfo || isCreatingIntent}
                            >
                                {isProcessing || isLoadingPaymentInfo || isCreatingIntent
                                    ? <Fragment><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...</Fragment>
                                    : (!hasPaymentMethod && selectedService.Price > 0) 
                                        ? 'Add Payment Method First' 
                                        : `Continue with ${selectedService.Name} Plan`}
                            </button>
                        )}
                        <p className="text-body-tertiary fs-10 mt-3 mb-0">Pricing reflects standard plans. Subscriptions renew automatically.</p>
                    </div>

                    {/* Right Column: Features List */}
                    <div className="col-lg-5 p-4 p-md-5 bg-light rounded-end-4 border-start d-flex flex-column justify-content-center">
                        <h5 className="fw-bold mb-4 fs-8">
                            {selectedService ? `${selectedService.Name} plan includes:` : 'Plan features:'}
                        </h5>
                        {selectedService && (
                            <ul className="list-unstyled m-0">
                                <li className="mb-3 d-flex align-items-start gap-3">
                                    <FontAwesomeIcon icon={faCheck} className="text-primary mt-1" />
                                    <span className="fs-9 text-dark fw-medium">Time Limit: {selectedService.DurationValue} {selectedService.DurationUnit}</span>
                                </li>
                                {selectedService.TypeId === 2 && (
                                    <li className="mb-3 d-flex align-items-start gap-3">
                                        <FontAwesomeIcon icon={faCheck} className="text-primary mt-1" />
                                        <span className="fs-9 text-dark fw-medium">User Limit: {selectedService.UserLimit} Users</span>
                                    </li>
                                )}
                                {selectedService.PlanDescriptionMappings.map((feature, index) => (
                                    <li key={index} className="mb-3 d-flex align-items-start gap-3">
                                        {feature.IsApply ? (
                                            <FontAwesomeIcon icon={faCheck} className="text-primary mt-1" />
                                        ) : (
                                            <FontAwesomeIcon icon={faTimes} className="text-body-tertiary mt-1 opacity-50" />
                                        )}
                                        <span className={`fs-9 ${feature.IsApply ? 'text-dark fw-medium' : 'text-body-tertiary'}`}>
                                            {feature.Description}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </CenteredModal>
        
        <CenteredModal
            show={showAddModal}
            onClose={() => setShowAddModal(false)}
            title="Add Payment Method"
            closeText=""
        >
            {clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <AddCardForm
                        clientSecret={clientSecret}
                        onSaveSuccess={handleSaveSuccess}
                        onCancel={() => setShowAddModal(false)}
                    />
                </Elements>
            ) : (
                <div className="text-center p-4"><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Loading...</div>
            )}
        </CenteredModal>

        <CenteredModal
            show={showConfirmPlanChange}
            title="Confirm Plan Change"
            onClose={() => !isProcessing && setShowConfirmPlanChange(false)}
            onConfirm={() => {
                setShowConfirmPlanChange(false);
                const proceedToCheckout = async () => {
                    setIsProcessing(true);
                    try {
                        const response = await api.post(`/user/stripe-payments/create-checkout-session/${selectedPlanId}/${serviceType}`);
                        window.location.href = response.data.Url;
                    } catch (error) {
                        toast.error(<span className="fs-9 fw-bold">Failed to initiate checkout.</span>);
                        setIsProcessing(false);
                    }
                };
                proceedToCheckout();
            }}
            isSubmitting={isProcessing}
            confirmText="Yes, proceed to payment!"
            closeText="Cancel"
        >
            <div className="p-3 text-center">
                <div 
                    className="mb-3 mx-auto d-flex align-items-center justify-content-center rounded-circle" 
                    style={{ 
                        width: '60px', 
                        height: '60px', 
                        background: '#fff3cd', 
                        color: '#ffc107' 
                    }}
                >
                    <FontAwesomeIcon icon={faTimesCircle} className="fs-3" />
                </div>
                <h5 className="fw-bold mb-2">Confirm Plan Change</h5>
                <p className="text-muted fs-8-1 mb-0">{confirmPlanData.text}</p>
            </div>
        </CenteredModal>
        </Fragment>
    );
}

export default UpgradePlanModal;
